import { useState, useEffect } from 'react';
import { getUsers, register, updateUser, deleteUser, reactivateUser } from '../lib/api';
import { useAuth } from '../lib/auth';
import { validatePassword } from '../lib/passwordValidation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, MoreHorizontal, Pencil, Trash2, Users, Search, Loader2, UserCheck, UserX } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const roles = [
  { value: 'admin', label: 'Admin', color: 'bg-red-100 text-red-700' },
  { value: 'hr', label: 'HR', color: 'bg-pink-100 text-pink-700' },
  { value: 'project_manager', label: 'Project Manager', color: 'bg-orange-100 text-orange-700' },
  { value: 'supervisor', label: 'Supervisor', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'venditore', label: 'Operativo', color: 'bg-green-100 text-green-700' },
  { value: 'cliente', label: 'Cliente', color: 'bg-purple-100 text-purple-700' },
  { value: 'account', label: 'Account', color: 'bg-orange-100 text-orange-700' },
];

export default function UsersPage() {
  const { hasRole } = useAuth();
  const isHrOnly = hasRole(['hr']) && !hasRole(['admin']);
  const availableRoles = isHrOnly ? roles.filter(r => r.value !== 'admin') : roles;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: ['venditore'],  // Array di ruoli
    is_active: true,
  });

  useEffect(() => {
    loadUsers();
  }, [showInactive]);

  const loadUsers = async () => {
    try {
      // Carica tutti gli utenti (inclusi inattivi se showInactive è true)
      const res = await getUsers(null, showInactive);
      setUsers(res.data);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare gli utenti', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        roles: user.roles || (user.role ? [user.role] : ['venditore']),  // Supporta sia roles che role singolo
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        roles: ['venditore'],
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const toggleRole = (roleValue) => {
    setFormData(prev => {
      const newRoles = prev.roles.includes(roleValue)
        ? prev.roles.filter(r => r !== roleValue)
        : [...prev.roles, roleValue];
      // Assicurati che ci sia almeno un ruolo
      return { ...prev, roles: newRoles.length > 0 ? newRoles : prev.roles };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (formData.roles.length === 0) {
        toast({ title: 'Errore', description: 'Seleziona almeno un ruolo', variant: 'destructive' });
        setSaving(false);
        return;
      }
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          roles: formData.roles,
          is_active: formData.is_active,
        };
        await updateUser(editingUser.id, updateData);
        toast({ title: 'Utente aggiornato' });
      } else {
        if (!formData.password) {
          toast({ title: 'Errore', description: 'La password è obbligatoria', variant: 'destructive' });
          setSaving(false);
          return;
        }
        await register(formData);
        toast({ title: 'Utente creato' });
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Disattivare l'utente "${user.name}"?\n\nL'utente non verrà eliminato ma disattivato per mantenere l'integrità dei dati storici.`)) return;
    try {
      await deleteUser(user.id);
      toast({ title: 'Utente disattivato', description: 'L\'utente è stato disattivato ma i suoi dati storici sono preservati.' });
      loadUsers();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile disattivare l\'utente', variant: 'destructive' });
    }
  };

  const handleReactivate = async (user) => {
    if (!window.confirm(`Riattivare l'utente "${user.name}"?`)) return;
    try {
      await reactivateUser(user.id);
      toast({ title: 'Utente riattivato' });
      loadUsers();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile riattivare l\'utente', variant: 'destructive' });
    }
  };

  const getRoleInfo = (role) => roles.find((r) => r.value === role) || { label: role, color: 'bg-gray-100 text-gray-700' };
  
  const getUserRoles = (user) => {
    // Supporta sia roles (nuovo) che role (vecchio)
    if (user.roles && user.roles.length > 0) {
      return user.roles;
    }
    if (user.role) {
      return [user.role];
    }
    return [];
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase());
    // Supporta sia roles (array) che role (singolo)
    const userRoles = getUserRoles(u);
    const matchesRole = !filterRole || userRoles.includes(filterRole);
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utenti</h1>
          <p className="text-gray-500">Gestisci gli utenti del sistema</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Utente
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {roles.map((role) => {
          // Conta utenti che hanno questo ruolo (supporta sia role singolo che roles array)
          const count = users.filter((u) => getUserRoles(u).includes(role.value)).length;
          return (
            <Card key={role.value}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{count}</div>
                <Badge className={role.color}>{role.label}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca utenti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <Select value={filterRole || "all"} onValueChange={(v) => setFilterRole(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtra per ruolo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i ruoli</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant={showInactive ? "default" : "outline"} 
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
            >
              <UserX className="w-4 h-4 mr-2" />
              {showInactive ? "Nascondi disattivati" : "Mostra disattivati"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-4 text-gray-500">Nessun utente trovato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruoli</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => {
                  const userRoles = getUserRoles(user);
                  const targetIsAdmin = userRoles.includes('admin');
                  const canManageUser = !isHrOnly || !targetIsAdmin;
                  return (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {userRoles.map(role => {
                            const roleInfo = getRoleInfo(role);
                            return (
                              <Badge key={role} className={roleInfo.color}>{roleInfo.label}</Badge>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <UserCheck className="w-4 h-4" />
                            Attivo
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <UserX className="w-4 h-4" />
                            Disattivo
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {canManageUser ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenDialog(user)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifica
                              </DropdownMenuItem>
                              {user.is_active !== false ? (
                                <DropdownMenuItem onClick={() => handleDelete(user)} className="text-orange-600">
                                  <UserX className="w-4 h-4 mr-2" />
                                  Disattiva
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleReactivate(user)} className="text-green-600">
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Riattiva
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-xs text-gray-400" title="Solo Admin può gestire questo utente">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Modifica i dati dell\'utente' : 'Crea un nuovo utente nel sistema'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="user-email"
              />
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  data-testid="user-password"
                />
                {formData.password && (
                  <div className="text-xs space-y-1 mt-2">
                    {(() => {
                      const { rules } = validatePassword(formData.password);
                      return (
                        <>
                          <p className={rules.minLength ? 'text-green-600' : 'text-gray-500'}>
                            {rules.minLength ? '✓' : '○'} Almeno 8 caratteri
                          </p>
                          <p className={rules.hasUppercase ? 'text-green-600' : 'text-gray-500'}>
                            {rules.hasUppercase ? '✓' : '○'} Almeno una maiuscola
                          </p>
                          <p className={rules.hasSpecial ? 'text-green-600' : 'text-gray-500'}>
                            {rules.hasSpecial ? '✓' : '○'} Almeno un carattere speciale
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Ruoli * (seleziona almeno uno)</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                {availableRoles.map((r) => {
                  const isSelected = formData.roles.includes(r.value);
                  return (
                    <div 
                      key={r.value}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-blue-50 border border-blue-200' : ''}`}
                      onClick={() => toggleRole(r.value)}
                    >
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => toggleRole(r.value)}
                        className="rounded"
                      />
                      <Badge className={r.color}>{r.label}</Badge>
                    </div>
                  );
                })}
              </div>
              {formData.roles.length === 0 && (
                <p className="text-xs text-red-600">⚠️ Seleziona almeno un ruolo</p>
              )}
              {formData.roles.length > 0 && (
                <p className="text-xs text-gray-500">
                  {formData.roles.length} ruol{formData.roles.length === 1 ? 'o' : 'i'} selezionat{formData.roles.length === 1 ? 'o' : 'i'}
                </p>
              )}
            </div>
            {editingUser && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_active">Utente attivo</Label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingUser ? 'Salva Modifiche' : 'Crea Utente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
